import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import axios from 'axios';
import RatingStars from '../components/RatingStars';
import { recordVisit, invalidateRatingMap } from '../utils/stats';
import { useSubscription } from '../contexts/SubscriptionContext';
import '../styles/reader.css';

/* ── Icons ── */
const IconPlay = () => (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>);
const IconStop = () => (<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1" /></svg>);
const IconSpeaker = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>);
const IconDragArrow = ({ flip = false }) => (
  <svg viewBox="0 0 64 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    {flip ? (
      <>
        <circle cx="60" cy="8" r="1.2" fill="currentColor" stroke="none"/>
        <line x1="56" y1="8" x2="14" y2="8"/>
        <path d="M20 3 L10 8 L20 13"/>
      </>
    ) : (
      <>
        <circle cx="4" cy="8" r="1.2" fill="currentColor" stroke="none"/>
        <line x1="8" y1="8" x2="50" y2="8"/>
        <path d="M44 3 L54 8 L44 13"/>
      </>
    )}
  </svg>
);
const IconSinglePage = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <line x1="9" y1="8" x2="15" y2="8" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);
const IconDoublePage = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="2" width="10" height="20" rx="1" /><rect x="13" y="2" width="10" height="20" rx="1" />
    <line x1="4" y1="7" x2="8" y2="7" /><line x1="4" y1="11" x2="8" y2="11" />
    <line x1="16" y1="7" x2="20" y2="7" /><line x1="16" y1="11" x2="20" y2="11" />
  </svg>
);

/* ── Sample data ── */
const SAMPLE_BOOK = {
  _id: 'sample',
  title: 'The Crocodile Who Closed the Lake',
  titleAr: 'التمساح الذي أغلق البحيرة',
  author: 'Deenlit Stories',
  coverImage: '/uploads/books/crocodile/cover-en.png',
  coverImageAr: '/uploads/books/crocodile/cover-ar.png',
  pages: [
    { pageNumber: 0, textEn: 'The Crocodile Who Closed the Lake', textAr: 'التمساح الذي أغلق البحيرة', image: '/uploads/books/crocodile/cover-en.png', audioEn: '/uploads/books/crocodile/audio-en-0.mp3', audioAr: '/uploads/books/crocodile/audio-ar-0.mp3' },
    { pageNumber: 1, textEn: 'Near a warm, slow-moving river lived a crocodile. He was strong — but oh, so lazy.\n\nAll day long, he did nothing but sleep beneath the trees, pulling fallen leaves over himself like a cosy blanket.\n\nFishing? That was far too much effort.\n\n"I wish the fish would come to me," he grumbled.', textAr: 'عاش تمساح قرب نهر دافئ بطيء الجريان.\nكان قويًا... لكنه كان كسولًا جدًا.\n\nكان يقضي يومه كله نائمًا تحت الأشجار، ويسحب الأوراق المتساقطة فوقه كأنها غطاء دافئ.\n\nأما الصيد؟ فذلك كان يتطلب جهدًا كبيرًا جدًا.\n\nتذمر قائلًا:\n"ليت السمك يأتي إليَّ بنفسه."', image: '/uploads/books/crocodile/page-1.png', audioEn: '/uploads/books/crocodile/audio-en-1.mp3', audioAr: '/uploads/books/crocodile/audio-ar-1.mp3' },
  ],
};

/* ── Page component — forwardRef is REQUIRED by react-pageflip
   data-density="hard" = stiff cover flip (front/back covers)
   data-density={softDensity} = realistic page peel (content pages)   ── */
const Page = forwardRef(({ children, density = 'soft', className = '' }, ref) => (
  <div
    className={`flip-page ${className}`}
    ref={ref}
    data-density={density}
  >
    <div className="flip-page-content">
      {children}
    </div>
  </div>
));

/* ============================================================ */
export default function Reader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sub = useSubscription();
  // The subscribe dialog is mounted outside the full-screen element, so it would be
  // invisible while the reader is full screen. Leave full screen first.
  const openSubscribeGate = useCallback((returnPath) => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {}).finally(() => sub.openGate(returnPath));
    else sub.openGate(returnPath);
  }, [sub]);
  // Preview state: when the visitor isn't subscribed (no token, or
  // logged-in-but-unsubscribed), the Reader fetches /preview which
  // returns the cover + first 3 content pages only, and we open the
  // paywall gate when they reach the last preview page.
  const [book, setBook] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [lang, setLang] = useState('en');
  const [orientation, setOrientation] = useState('double'); // 'single' | 'double'
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [dims, setDims] = useState({ w: 500, h: 650 });
  const [layoutMode, setLayoutMode] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const lastAutoLayoutModeRef = useRef(null);

  const flipRef = useRef(null);
  const tourOriginRef = useRef(null);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const syncFullscreen = () => setIsFullscreen(document.fullscreenElement === wrapperRef.current);
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);

  // Saved reading progress (for the Resume-from-page banner)
  const [savedProgress, setSavedProgress] = useState(null); // { page, language } or null
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  // Admins can open a draft from the book editor without publishing it. That view loads
  // through an admin-only endpoint and never records visits, progress, ratings or rewards.
  const adminPreview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('adminPreview') === '1';
  const [readerToken, setAuthToken] = useState(() =>
    (typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null)
  );
  const authToken = adminPreview ? null : readerToken;
  const flipPagesLengthRef = useRef(0);

  // Rating prompt — shown when the child reaches the last page
  const [userRating, setUserRating] = useState(0);
  const [ratingPromptOpen, setRatingPromptOpen] = useState(false);
  const [ratingDismissed, setRatingDismissed] = useState(false);
  const [ratingSavedAt, setRatingSavedAt] = useState(0);

  // Keep token in sync with login/logout events (storage from another tab, or local dispatches)
  useEffect(() => {
    const refresh = () => setAuthToken(localStorage.getItem('adeeb-token'));
    window.addEventListener('storage', refresh);
    window.addEventListener('adeeb-auth-change', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('adeeb-auth-change', refresh);
    };
  }, []);

  // TTS + word highlighting
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [audioMessage, setAudioMessage] = useState('');
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [wordTimings, setWordTimings] = useState([]);
  const audioRef = useRef(null);
  const utteranceRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const autoAdvanceRef = useRef(false);
  const manualNarrationPendingRef = useRef(false);
  const manualNarrationTimerRef = useRef(null);
  const lastPlayedTextRef = useRef('');
  const lastPlayedPageRef = useRef(null);
  const pageNumberMapRef = useRef([]);
  const isSpeakingRef = useRef(false);
  const stopPlaybackRef = useRef(null);
  const resumeStateRef = useRef(null); // { pageNumber, time } captured on user pause

  /* ── Load book ── */
  useEffect(() => {
    if (id === 'sample' || id === '1') {
      setBook(SAMPLE_BOOK); setLoadError(null); setLoading(false); return;
    }
    // Wait for subscription status before deciding which endpoint to hit.
    // Anonymous visitors (no token) skip the wait — sub.loading flips to
    // false immediately for them.
    if (sub.loading) return;

    let alive = true;
    setLoading(true); setBook(null); setLoadError(null);
    const token = localStorage.getItem('adeeb-token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    // Subscribed users (incl. admins, students, staff per SubscriptionContext)
    // fetch the full book. Everyone else gets the public preview — cover
    // + first 3 content pages — so they can sample before paying.
    const previewUrl = `/api/books/${id}/preview`;
    const request = async (url, options) => {
      try { return await axios.get(url, options); }
      catch (error) {
        if (!alive || (error.response && error.response.status < 500)) throw error;
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!alive) throw error;
        return axios.get(url, options);
      }
    };
    const load = async () => {
      try {
        let response;
        if (adminPreview) response = await request(`/api/admin/books/${id}/reader-preview`, { headers });
        else if (sub.active) {
          try { response = await request(`/api/books/${id}`, { headers }); }
          catch (error) {
            if (![401, 402, 403].includes(error.response?.status)) throw error;
            response = await request(previewUrl, {});
          }
        } else response = await request(previewUrl, {});
        if (alive) { setBook(response.data); if (!adminPreview) recordVisit(id); }
      } catch (error) {
        if (alive) setLoadError(error.response?.status === 404 ? 'not-found' : 'unavailable');
      } finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [id, sub.active, sub.loading, loadAttempt, adminPreview]);

  /* ── Scroll wrapper to bottom whenever the reader opens ──
     Retry until the flipbook's async layout settles (scrollHeight
     stops growing) so we always land at the real bottom. */
  useEffect(() => {
    if (loading || !book) return;
    const w = wrapperRef.current;
    if (!w) return;
    let cancelled = false;
    let lastHeight = -1;
    let stable = 0;
    const start = performance.now();
    const tick = () => {
      if (cancelled) return;
      const h = w.scrollHeight;
      w.scrollTop = h;
      if (h === lastHeight) stable++; else { stable = 0; lastHeight = h; }
      const elapsed = performance.now() - start;
      if (stable >= 3 && elapsed > 400) return; // settled
      if (elapsed > 3000) return; // cap at 3s
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => { cancelled = true; };
  }, [loading, book, id]);

  /* ── Load saved progress (if logged in) ── */
  useEffect(() => {
    if (!authToken || !id) return;
    let alive = true;
    axios
      .get(`/api/progress/${id}`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(({ data }) => {
        if (!alive || !data || typeof data.page !== 'number' || data.page <= 0) return;
        setSavedProgress({ page: data.page, language: data.language || 'en' });
        setShowResumeBanner(true);
      })
      .catch(() => { /* 404 = no progress yet */ });
    return () => { alive = false; };
  }, [id, authToken]);

  /* ── Responsive sizing — fill as much screen as possible ── */
  useEffect(() => {
    const calc = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Preserve current page across any auto-orientation change
      // (rotation, window resize crossing a breakpoint, etc.)
      if (currentPageRef.current > 0) {
        restorePageRef.current = currentPageRef.current;
      }

      let w, h;

      // Helper: derive per-page width from a target total book width in double mode
      const perPage = (totalBook) => Math.floor(totalBook / 2);
      const isPortrait = vh > vw;
      // Desktop detection:
      //   • Big window (≥1280) with laptop/monitor aspect (<0.62), OR
      //   • Mouse/trackpad present (pointer:fine) at ≥1024 — catches
      //     narrow desktop Chrome windows without affecting touch tablets.
      const isPointerFine = window.matchMedia('(pointer: fine)').matches;
      const isDesktop =
        (vw >= 1280 && (vh / vw) < 0.62) ||
        (vw >= 1024 && isPointerFine);
      // Real touch hardware (phone). Desktop browsers always report pointer:fine
      // so resizing a desktop window can never match this.
      const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
      const isPhoneLandscape = isTouch && !isPortrait && vh < 500;

      // Default layout mode (cleared before branches set their own)
      let newLayoutMode = '';
      let branchTaken = '';

      if (vw < 480) {
        w = vw - 16;
        h = vh - 150;
        newLayoutMode = 'phone-portrait';
        branchTaken = 'A:phone-portrait';
      } else if (isPhoneLandscape) {
        w = perPage(vw - 130);
        h = vh - 95;
        newLayoutMode = 'phone-landscape';
        branchTaken = 'B:phone-landscape';
      } else if (!isDesktop && isPortrait) {
        // Any tablet-size portrait (iPad mini, iPad Air, iPad Pro, split-view, etc.)
        // → SINGLE page mode so cover + all pages fill the full width.
        w = Math.min(vw - 20, 980);
        h = vh - 115;
        newLayoutMode = 'tablet-portrait';
        branchTaken = 'C:tablet-portrait';
      } else if (!isDesktop) {
        // Tablet landscape → double page with side arrows.
        // vw-180 leaves ~40px between each nav arrow and the viewport edge.
        w = perPage(Math.min(vw - 180, 880));
        h = vh - 145;
        newLayoutMode = 'tablet-landscape';
        branchTaken = 'D:tablet-landscape';
      } else {
        w = perPage(Math.min(vw - 160, 1640));
        h = vh - 90;
        branchTaken = 'F:desktop';
      }

      // Keep the regular page shape in two-page mode. A landscape book gets
      // its wider dimensions only when the reader is showing one page.
      if (!isPhoneLandscape) {
        const clampRatio = isDesktop ? 0.85 : 0.72;
        const maxW = Math.floor(h * clampRatio);
        if (w > maxW) w = maxW;
      }

      // Enforce minimums so the library never gets invalid dimensions
      const finalW = Math.max(w, 200);
      const finalH = Math.max(h, 250);
      setDims({ w: finalW, h: finalH });
      setLayoutMode(newLayoutMode);
      // Determine orientation that WILL be set (matches the branches above)
      const finalOrientation =
        book?.format === 'landscape' ? 'single' :
        vw < 480 ? 'single' :
        isPhoneLandscape ? 'double' :
        (!isDesktop && isPortrait) ? 'single' :
        'double';
      // Set the device default on first load or a real layout change. Keep a
      // reader's one-page choice through fullscreen and small viewport resizes.
      if (lastAutoLayoutModeRef.current !== newLayoutMode) {
        lastAutoLayoutModeRef.current = newLayoutMode;
        setOrientation(finalOrientation);
      }
      setDebugInfo({
        vw, vh,
        isPortrait,
        isDesktop,
        isTouch,
        isPhoneLandscape,
        finalW, finalH,
        layoutMode: newLayoutMode,
        finalOrientation,
        branchTaken,
      });
    };
    calc();
    let resizeTimer;
    const debounced = () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(calc, 300); };
    window.addEventListener('resize', debounced);
    return () => { clearTimeout(resizeTimer); window.removeEventListener('resize', debounced); };
  }, [book?.format]);

  /* ── Auto-save progress (debounced + flush-on-leave) ── */
  // Latest unsent payload — also fires on unmount so SPA navigation
  // before the 1.2s timer doesn't drop the user's progress.
  const pendingProgressRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    const token = localStorage.getItem('adeeb-token');
    if (!token || !id || currentPage <= 0 || tourOriginRef.current !== null) return;
    pendingProgressRef.current = {
      bookId: id,
      page: currentPage,
      language: lang,
      totalPages: flipPagesLengthRef.current,
    };
    const t = setTimeout(() => {
      const payload = pendingProgressRef.current;
      if (!payload) return;
      pendingProgressRef.current = null;
      axios.post('/api/progress', payload, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => { /* swallow */ });
    }, 1200);
    return () => clearTimeout(t);
  }, [currentPage, lang, authToken, id, loading]);

  // Flush any pending save when we unmount (SPA navigation away from the
  // Reader). beforeunload below covers tab-close.
  useEffect(() => {
    return () => {
      const payload = pendingProgressRef.current;
      if (!payload) return;
      const token = localStorage.getItem('adeeb-token');
      if (!token) return;
      pendingProgressRef.current = null;
      axios.post('/api/progress', payload, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    };
  }, []);

  /* ── Load this user's existing rating for the book (if any) ── */
  useEffect(() => {
    if (!authToken || !id) { setUserRating(0); return; }
    let alive = true;
    axios
      .get(`/api/ratings/me/${encodeURIComponent(id)}`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(({ data }) => { if (alive) setUserRating(data?.value || 0); })
      .catch(() => { if (alive) setUserRating(0); });
    return () => { alive = false; };
  }, [authToken, id]);

  /* ── Detect end-of-book → open rating prompt ── */
  useEffect(() => {
    const total = flipPagesLengthRef.current;
    if (!total || ratingDismissed || tourOriginRef.current !== null) return;
    const reachedEnd = currentPage + 1 >= total;
    if (reachedEnd) setRatingPromptOpen(true);
  }, [currentPage, ratingDismissed]);

  const submitRating = useCallback(async (value) => {
    setUserRating(value);
    if (!authToken || !id) return;
    try {
      await axios.post(
        '/api/ratings',
        { bookId: id, value },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setRatingSavedAt(Date.now());
      // Refresh the shared rating cache so the new average shows up
      // on every BookCard / details modal without a full page reload.
      invalidateRatingMap();
    } catch {
      /* ignore — UI keeps the chosen value */
    }
  }, [authToken, id]);

  const closeRatingPrompt = useCallback(() => {
    setRatingPromptOpen(false);
    setRatingDismissed(true);
  }, []);

  /* ── Save-on-exit: best-effort flush when the tab closes or user navigates away ── */
  useEffect(() => {
    if (!authToken || !id) return;
    const flush = () => {
      const page = tourOriginRef.current ?? currentPageRef.current;
      if (page <= 0) return;
      try {
        const blob = new Blob(
          [JSON.stringify({ bookId: id, page, language: lang, totalPages: flipPagesLengthRef.current })],
          { type: 'application/json' }
        );
        // keepalive flush carries credentials in headers/cookies, never URLs.
        const profileId = (() => {
          try {
            const raw = localStorage.getItem('adeeb-active-profile');
            return raw ? JSON.parse(raw)?._id : null;
          } catch { return null; }
        })();
        fetch('/api/progress', { method: 'POST', body: blob, keepalive: true, credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...(profileId ? { 'X-Profile-Id': profileId } : {}), ...(authToken !== 'cookie-session' ? { Authorization: `Bearer ${authToken}` } : {}) } }).catch(() => {});
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, [authToken, id, lang]);

  const resumeToSaved = useCallback(() => {
    if (!savedProgress) return;
    const target = savedProgress.page;
    if (savedProgress.language && savedProgress.language !== lang) {
      restorePageRef.current = target; // language swap rebuilds pages; this effect will seek after
      setLang(savedProgress.language);
    } else {
      const api = flipRef.current?.pageFlip?.();
      try {
        if (api && typeof api.turnToPage === 'function') api.turnToPage(target);
        else if (api) api.flip(target, 'corner');
      } catch { /* ignore */ }
    }
    setShowResumeBanner(false);
  }, [savedProgress, lang]);

  /* ── Flipbook events ── */
  const onFlip = useCallback((e) => {
    const next = e?.data ?? 0;
    // Keep stable page indexes for saved progress, but skip the decorative
    // flyleaf when a single-page reader swipes or uses navigation.
    if (orientation === 'single' && next === 1 && book?.pages?.some(p => p.pageNumber === 0)) {
      const target = currentPageRef.current > 1 ? 0 : 2;
      requestAnimationFrame(() => flipRef.current?.pageFlip().turnToPage(target));
      return;
    }
    const continueNarration = manualNarrationPendingRef.current ||
      (!!audioRef.current && !audioRef.current.ended && autoAdvanceRef.current);
    manualNarrationPendingRef.current = false;
    if (manualNarrationTimerRef.current) clearTimeout(manualNarrationTimerRef.current);
    if (audioRef.current && !audioRef.current.ended) stopPlaybackRef.current?.();
    setCurrentPage(next);
    if (continueNarration && !book?.preview) {
      manualNarrationTimerRef.current = setTimeout(() => {
        const candidates = orientation === 'double' ? [next, next + 1] : [next];
        const chosenIdx = candidates.find(i => pageTextMapRef.current[i]);
        if (chosenIdx == null) return;
        activePlayIdxRef.current = chosenIdx;
        setActivePlayIdx(chosenIdx);
        resumeStateRef.current = null;
        autoAdvanceRef.current = true;
        startPlaybackRef.current?.(pageTextMapRef.current[chosenIdx], 0, chosenIdx);
      }, 450);
    }
    // Preview mode: pages 1 and 2 are free to read; the gate only
    // fires once the visitor flips PAST page 2 and lands on the
    // hard-density "End of preview" trigger page (which is always
    // the last index in flipPages). currentPage reports that index
    // directly in single-page mode and when react-pageflip renders
    // the trigger solo (hard cover) in double-page mode.
    if (book?.preview && tourOriginRef.current === null) {
      const total = flipPagesLengthRef.current || 0;
      if (total > 0 && next >= total - 1) {
        openSubscribeGate(`/read/${id}`);
      }
    }
  }, [book, sub, id, orientation, openSubscribeGate]);

  /* Block user input during the flipbook's settle-window. If the user
     swipes/clicks before react-pageflip's resize-observer has finished
     measuring (mostly an issue right after mount or after a dims-key
     remount), the first flip animates against partial measurements and
     the .leather-frame visibly jumps. 2s gives plenty of headroom for
     slower devices / large cover images to finish painting.

     Reset to false whenever dims/orientation change — those rebuild the
     flipbook from scratch via its key, so init re-runs. */
  const [interactionReady, setInteractionReady] = useState(false);
  const readyTimerRef = useRef(null);
  useEffect(() => {
    setInteractionReady(false);
    if (readyTimerRef.current) clearTimeout(readyTimerRef.current);
    // Belt-and-braces: if onInit somehow doesn't fire (older mobile
    // browsers etc.), we still flip ready on a slightly longer fallback
    // so the user is never permanently locked out.
    readyTimerRef.current = setTimeout(() => setInteractionReady(true), 2000);
    return () => { if (readyTimerRef.current) clearTimeout(readyTimerRef.current); };
  }, [dims.w, dims.h, orientation]);

  const onInit = useCallback(() => {
    if (readyTimerRef.current) clearTimeout(readyTimerRef.current);
    readyTimerRef.current = setTimeout(() => setInteractionReady(true), 1500);
  }, []);

  const goNext = useCallback(() => {
    if (!interactionReady) return;
    manualNarrationPendingRef.current = !!audioRef.current && !audioRef.current.ended && autoAdvanceRef.current;
    stopPlaybackRef.current?.();
    flipRef.current?.pageFlip().flipNext();
  }, [interactionReady]);

  const goPrev = useCallback(() => {
    if (!interactionReady) return;
    manualNarrationPendingRef.current = !!audioRef.current && !audioRef.current.ended && autoAdvanceRef.current;
    stopPlaybackRef.current?.();
    flipRef.current?.pageFlip().flipPrev();
  }, [interactionReady]);

  /* ── Keyboard ── */
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [goNext, goPrev]);

  /* ── TTS ── */
  const stopHighlight = useCallback(() => {
    if (highlightTimerRef.current) {
      clearInterval(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    setActiveWordIndex(-1);
    setWordTimings([]);
  }, []);

  // Tear down current audio without touching the auto-advance flag —
  // used when chaining to the next page's audio.
  const stopAudioInternal = useCallback(() => {
    if (audioRef.current) { audioRef.current.onended = null; audioRef.current.onerror = null; audioRef.current.pause(); audioRef.current = null; }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    stopHighlight();
    setIsSpeaking(false);
    // NOTE: deliberately NOT clearing activePlayIdx here. playPageAudio /
    // playTTS call stopAudioInternal to tear down the previous audio
    // before starting fresh — clearing the pin in that path wipes the
    // index toggleAudio just set, and the highlighter falls back to the
    // candidate scan (which picks the left page in text-text layout).
    // The pin is cleared by the user-initiated stopAudio path instead.
  }, [stopHighlight]);

  // User-initiated stop — also cancels continuous reading and captures
  // the current playback position so a follow-up play resumes from it.
  const stopAudio = useCallback(() => {
    autoAdvanceRef.current = false;
    if (audioRef.current && lastPlayedTextRef.current) {
      const t = audioRef.current.currentTime;
      resumeStateRef.current = { pageNumber: lastPlayedPageRef.current, time: isFinite(t) ? t : 0 };
    } else {
      resumeStateRef.current = null;
    }
    lastPlayedTextRef.current = '';
    lastPlayedPageRef.current = null;
    stopAudioInternal();
    // Release the highlighter pin only when the user explicitly stops —
    // not when we're tearing down to switch to a new audio source.
    activePlayIdxRef.current = null;
    setActivePlayIdx(null);
  }, [stopAudioInternal]);

  /* ── Preserve current page across language / orientation toggles ── */
  useEffect(() => { stopPlaybackRef.current = stopAudio; }, [stopAudio]);
  useEffect(() => () => { stopPlaybackRef.current?.(); }, [id]);

  const restorePageRef = useRef(null);
  const currentPageRef = useRef(0);
  // Keep currentPageRef in sync with the React state so the resize-triggered
  // dims calc can read the latest page index synchronously.
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  const switchLang = useCallback((newLang) => {
    if (newLang === lang) return;
    restorePageRef.current = currentPage;
    stopAudio();
    // A paused position belongs to the old language's recording. Starting
    // the translated recording must begin at its own opening, not that time.
    resumeStateRef.current = null;
    setLang(newLang);
  }, [lang, currentPage, stopAudio]);

  const switchOrientation = useCallback((newOrientation) => {
    if (newOrientation === orientation) return;
    let target = currentPage;
    if (newOrientation === 'single' && currentPage > 0 && currentPage % 2 === 1) {
      // Double→single: double-mode reports left-of-spread (illustration). User was reading text → +1
      target = currentPage + 1;
    }
    restorePageRef.current = target;
    setOrientation(newOrientation);
  }, [orientation, currentPage]);

  // A landscape book has no one/two-page choice: it always reads full width.
  useEffect(() => {
    if (book?.format === 'landscape' && orientation !== 'single') switchOrientation('single');
  }, [book?.format, orientation, switchOrientation]);

  useEffect(() => {
    const target = restorePageRef.current;
    if (target == null || target === 0) {
      restorePageRef.current = null;
      return;
    }
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      try {
        const api = flipRef.current?.pageFlip?.();
        if (api) {
          const current = api.getCurrentPageIndex?.();
          if (current !== target) {
            if (typeof api.turnToPage === 'function') api.turnToPage(target);
            else api.flip(target, 'corner');
          } else {
            restorePageRef.current = null;
            clearInterval(interval);
          }
        }
      } catch { /* ignore */ }
      if (attempts >= 40) {
        restorePageRef.current = null;
        clearInterval(interval);
      }
    }, 40);
    return () => clearInterval(interval);
  }, [lang, orientation, dims.w, dims.h]);

  // Flipbook index of the page whose play button started the current
  // audio. Pinned via toggleAudio(preferredIdx); ensures the highlighter
  // targets the correct page in text-text layout (where both sides of a
  // spread carry tts-word spans). Cleared when audio stops.
  const activePlayIdxRef = useRef(null);
  const [activePlayIdx, setActivePlayIdx] = useState(null);

  /* Start a timer that checks audio.currentTime and updates the highlighted word */
  /* Imperatively toggle .word-active in the DOM so activeWordIndex updates
     don't trigger a React re-render of the reading-scroll (which would
     reset the user's scroll position). */
  useEffect(() => {
    document.querySelectorAll('.tts-word.word-active').forEach(el => el.classList.remove('word-active'));
    if (activeWordIndex < 0 || !isSpeaking) return;
    // Prefer the page that explicitly started playback. Fall back to the
    // currentPage neighbours scan for the intro / autoplay paths that
    // don't pin an index.
    let target = null;
    if (typeof activePlayIdx === 'number') {
      target = document.querySelector(`[data-flip-idx="${activePlayIdx}"]`);
    }
    if (!target) {
      const candidates = [currentPage, currentPage + 1, currentPage - 1];
      for (const idx of candidates) {
        target = document.querySelector(`[data-flip-idx="${idx}"]`);
        if (target) break;
      }
    }
    if (!target) return;
    const span = target.querySelector(`.tts-word[data-idx="${activeWordIndex}"]`);
    if (span) span.classList.add('word-active');
  }, [activeWordIndex, currentPage, isSpeaking, activePlayIdx]);

  const startHighlightSync = useCallback((audio, timings) => {
    setWordTimings(timings);
    setActiveWordIndex(-1);
    if (highlightTimerRef.current) clearInterval(highlightTimerRef.current);
    highlightTimerRef.current = setInterval(() => {
      if (!audio || audio.paused) return;
      const t = audio.currentTime;
      let idx = -1;
      for (let i = 0; i < timings.length; i++) {
        if (t >= timings[i].offset && t < timings[i].offset + timings[i].duration + 0.15) {
          idx = i;
        }
      }
      setActiveWordIndex(idx);
    }, 50);
  }, []);


  // Find the DB page whose language-specific text matches the given text
  const findDbPageForText = useCallback((text, flipIdx) => {
    const pageNumber = typeof flipIdx === 'number' ? pageNumberMapRef.current[flipIdx] : null;
    if (pageNumber != null) {
      const exact = (book?.pages || []).find((p) => p.pageNumber === pageNumber);
      if (exact) return exact;
    }
    return (book?.pages || []).find((p) => (lang === 'ar' ? p.textAr : p.textEn) === text);
  }, [book, lang]);

  // Start playing the given text (prefers manual audio URL, otherwise TTS).
  // Forward-declared as a ref so onAudioDone can call it without a circular dep.
  const startPlaybackRef = useRef(null);

  const onAudioDone = useCallback(() => {
    const shouldAdvance = autoAdvanceRef.current;
    setIsSpeaking(false);
    stopHighlight();
    if (!shouldAdvance) return;

    // Defer so React finishes the isSpeaking=false re-render (which rebuilds
    // flipPages) before we ask the flipbook to animate.
    setTimeout(() => {
      if (!autoAdvanceRef.current) return;
      const api = flipRef.current?.pageFlip?.();
      if (!api) { autoAdvanceRef.current = false; return; }

      // text-text + double-page: both halves of a spread carry their
      // own text, but the default flipNext jumps to the next spread, so
      // the right page of the just-played spread gets skipped. If the
      // page that just finished was the LEFT half of the current spread,
      // play the RIGHT half next without flipping.
      //
      // react-pageflip sets `currentPage` (via onFlip's e.data) to the
      // LEFT page index of the active spread — so "we just played the
      // left page" is exactly `playedIdx === currentPage`. (The naive
      // playedIdx % 2 === 0 check fails because the hard-density cover
      // sits alone at index 0 and shifts every subsequent pair's parity.)
      if (book?.pageLayout === 'text-text' && orientation === 'double') {
        const playedIdx = activePlayIdxRef.current ?? currentPageRef.current;
        if (playedIdx != null && playedIdx === currentPageRef.current) {
          const siblingIdx = playedIdx + 1;
          const siblingText = pageTextMapRef.current[siblingIdx];
          if (siblingText && pageNumberMapRef.current[siblingIdx] !== lastPlayedPageRef.current) {
            activePlayIdxRef.current = siblingIdx;
            setActivePlayIdx(siblingIdx);
            startPlaybackRef.current?.(siblingText, 0, siblingIdx);
            return;
          }
        }
      }

      let steps = 0;
      const advance = () => {
        if (steps++ > 4 || !autoAdvanceRef.current) { autoAdvanceRef.current = false; return; }
        const before = currentPageRef.current;
        try { api.flipNext(); } catch { autoAdvanceRef.current = false; return; }
        setTimeout(() => {
          if (!autoAdvanceRef.current) return;
          const after = currentPageRef.current;
          if (after === before) { autoAdvanceRef.current = false; return; } // end of book
          const candidates = [after, after + 1];
          let nextText = '';
          let nextIdx = null;
          for (const i of candidates) {
            const t = pageTextMapRef.current[i];
            if (t && pageNumberMapRef.current[i] !== lastPlayedPageRef.current) { nextText = t; nextIdx = i; break; }
          }
          if (!nextText) { advance(); return; }
          // Update the highlighter pin to the new page BEFORE playback
          // starts, so the existing-audio tear-down inside startPlayback
          // doesn't leave the highlighter pointing at the previous page.
          activePlayIdxRef.current = nextIdx;
          setActivePlayIdx(nextIdx);
          startPlaybackRef.current?.(nextText, 0, nextIdx);
        }, 650); // flippingTime is 550ms
      };
      advance();
    }, 120);
  }, [stopHighlight, book, orientation]);

  // The audio-starter callbacks (playPageAudio / playTTS / playWithWebSpeech)
  // assign `a.onended = onAudioDone` but don't list onAudioDone in their
  // useCallback deps — so they capture the first-mount onAudioDone (when
  // book is still null) and never see the version recreated after the
  // book API call resolves. Stale closure means book.pageLayout is
  // always undefined inside the captured callback, the text-text sibling
  // path is skipped, and the auto-advance jumps the full spread instead
  // of reading the right page. Route through a ref so every audio
  // session reads the latest onAudioDone.
  const onAudioDoneRef = useRef(null);
  useEffect(() => { onAudioDoneRef.current = onAudioDone; }, [onAudioDone]);
  const onAudioDoneStable = useCallback(() => { onAudioDoneRef.current?.(); }, []);

  const narrationUnavailable = useCallback(() => {
    setIsSpeaking(false);
    setTtsLoading(false);
    setAudioMessage('Narration is not available for this page in this language yet.');
  }, []);
  const playTTS = narrationUnavailable;
  const playWithWebSpeech = narrationUnavailable;

  /* Callback ref that attaches native capture-phase listeners
     to stop pointer events from reaching StPageFlip's page-flip handlers */
  const stopFlipPropRef = useCallback((node) => {
    if (!node) return;
    const stop = (e) => { e.stopPropagation(); };
    node.addEventListener('mousedown', stop, true);
    node.addEventListener('pointerdown', stop, true);
    node.addEventListener('touchstart', stop, true);
  }, []);

  // Text still scrolls vertically; a short, deliberate horizontal swipe turns
  // the page instead of being swallowed by the text panel's input guard.
  const readingSwipeRef = useCallback((node) => {
    if (!node) return;
    stopFlipPropRef(node);
    let start = null;
    node.addEventListener('touchstart', (e) => {
      start = e.touches.length === 1
        ? { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() }
        : null;
    }, { capture: true, passive: true });
    node.addEventListener('touchmove', (e) => {
      if (!start || e.touches.length !== 1) { start = null; return; }
      const dx = Math.abs(e.touches[0].clientX - start.x);
      const dy = Math.abs(e.touches[0].clientY - start.y);
      if (dy > 10 && dy > dx) start = null;
    }, { passive: true });
    node.addEventListener('touchcancel', () => { start = null; }, { passive: true });
    node.addEventListener('touchend', (e) => {
      const origin = start; start = null;
      if (!origin || !e.changedTouches.length || Date.now() - origin.time > 600) return;
      const dx = e.changedTouches[0].clientX - origin.x;
      const dy = Math.abs(e.changedTouches[0].clientY - origin.y);
      const api = flipRef.current?.pageFlip?.();
      if (Math.abs(dx) < 18 || Math.abs(dx) < dy * 1.5 ||
          node.closest('.leather-frame')?.dataset.ready !== 'true' || api?.getState() !== 'read') return;
      manualNarrationPendingRef.current = !!audioRef.current && !audioRef.current.ended && autoAdvanceRef.current;
      stopPlaybackRef.current?.();
      if (dx < 0) api.flipNext(); else api.flipPrev();
    }, { passive: true });
  }, [stopFlipPropRef]);

  const playPageAudio = useCallback(async (url, text, seekTime = 0) => {
    stopAudioInternal();
    setAudioMessage('');
    const a = new Audio(url);
    audioRef.current = a;
    a.onended = onAudioDoneStable;
    a.onerror = () => {
      setIsSpeaking(false);
      if (audioRef.current === a) playWithWebSpeech(text);
    };
    // Load the whisper-generated word timings sidecar (same path, .mp3 → .json)
    let timings = null;
    try {
      const res = await fetch(url.replace(/\.mp3$/, '.json'));
      if (res.ok) timings = await res.json();
    } catch {}
    if (audioRef.current !== a) return;
    try {
      await a.play();
      if (audioRef.current !== a) { a.pause(); return; }
      if (seekTime > 0 && isFinite(seekTime)) {
        try { a.currentTime = seekTime; } catch {}
      }
      setIsSpeaking(true);
      if (timings && timings.length > 0) startHighlightSync(a, timings);
    } catch {
      if (audioRef.current === a) playWithWebSpeech(text);
    }
  }, [stopAudioInternal, playWithWebSpeech, startHighlightSync, onAudioDoneStable]);

  const startPlayback = useCallback((text, seekTime = 0, flipIdx = null) => {
    if (!text) return;
    lastPlayedTextRef.current = text;
    const dbPage = findDbPageForText(text, flipIdx);
    lastPlayedPageRef.current = dbPage?.pageNumber ?? null;
    const manualUrl = dbPage && (lang === 'ar' ? dbPage.audioAr : dbPage.audioEn);
    if (manualUrl) playPageAudio(manualUrl, text, seekTime);
    else playTTS(text, seekTime);
  }, [findDbPageForText, lang, playPageAudio, playTTS]);

  useEffect(() => { startPlaybackRef.current = startPlayback; }, [startPlayback]);

  useEffect(() => { isSpeakingRef.current = isSpeaking; }, [isSpeaking]);

  // If audio is playing when the page changes (manual flip), switch the
  // narrator to the new page's text so it follows what the user is viewing.
  // Auto-advance flips run while isSpeaking is false, so they bypass this.
  useEffect(() => {
    if (!isSpeakingRef.current) return;
    const candidates = [currentPage, currentPage + 1, currentPage - 1];
    let text = '';
    let chosenIdx = null;
    for (const i of candidates) {
      const t = pageTextMapRef.current[i];
      if (t) { text = t; chosenIdx = i; break; }
    }
    if (!text || pageNumberMapRef.current[chosenIdx] === lastPlayedPageRef.current) return;
    stopAudioInternal();
    resumeStateRef.current = null;
    // Re-pin the highlighter to the page we're switching to.
    activePlayIdxRef.current = chosenIdx;
    setActivePlayIdx(chosenIdx);
    startPlaybackRef.current?.(text, 0, chosenIdx);
  }, [currentPage, stopAudioInternal]);

  // Hard-stop audio if the user navigates away from the Reader (e.g. clicks
  // "Library" on the home button). Without this, the Audio element keeps
  // playing after unmount because nothing else tears it down.
  useEffect(() => {
    return () => {
      autoAdvanceRef.current = false;
      if (manualNarrationTimerRef.current) clearTimeout(manualNarrationTimerRef.current);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (highlightTimerRef.current) clearInterval(highlightTimerRef.current);
    };
  }, []);

  /* `preferredIdx` is the flipbook-page index of the button that fired
     the click — in text-text layout both pages of a spread carry their
     own play button, so we need to know which one was tapped instead of
     just guessing from currentPage. Falls back to the legacy nearest-
     neighbour scan when no index is supplied (still used by the intro
     page and the keyboard / programmatic paths). */
  const toggleAudio = useCallback((preferredIdx) => {
    if (isSpeaking) { stopAudio(); return; }
    let text = '';
    let chosenIdx = null;
    if (typeof preferredIdx === 'number') {
      const t = pageTextMapRef.current[preferredIdx];
      if (t) { text = t; chosenIdx = preferredIdx; }
    }
    if (!text) {
      // Look at current + neighbors; text page can be off by one in double-page mode
      const candidates = [currentPage, currentPage + 1, currentPage - 1];
      for (const i of candidates) {
        const t = pageTextMapRef.current[i];
        if (t) { text = t; chosenIdx = i; break; }
      }
    }
    if (!text) return;
    // Pin the highlighter to the exact page whose audio we're starting.
    // Without this, the highlight effect also runs the candidates scan
    // and in text-text layout (both spread sides have text) it lands on
    // the left page even when the right's button was tapped.
    activePlayIdxRef.current = chosenIdx;
    setActivePlayIdx(chosenIdx);
    autoAdvanceRef.current = true;
    const r = resumeStateRef.current;
    const seek = r && r.pageNumber === pageNumberMapRef.current[chosenIdx] ? r.time : 0;
    resumeStateRef.current = null;
    startPlayback(text, seek, chosenIdx);
  }, [isSpeaking, currentPage, stopAudio, startPlayback]);

  /* ── Helpers ── */
  const getText = (page) => (lang === 'ar' ? page.textAr : page.textEn) || '';
  const getHtml = (page) => (lang === 'ar' ? page.htmlAr : page.htmlEn) || '';
  const getImage = (page) => {
    if (lang === 'ar' && page.pageNumber === 0 && book?.coverImageAr) return book.coverImageAr;
    return page.image;
  };

  /* Render text as .tts-word spans. Highlighting (.word-active) is applied
     imperatively by the effect below so this render doesn't depend on
     activeWordIndex — keeps the memoized flipPages stable during playback. */
  const renderHighlightedText = (page, flipIdx) => {
    const html = getHtml(page);
    const text = getText(page);
    if (!html && !text) return null;
    const counter = { value: 0 };
    const body = html
      ? renderRichHtml(html, counter)
      : renderPlainText(text, counter);
    // <div> wrapper (not <p>) so nested <p>/block elements from the rich
    // editor remain valid — a <p> inside a <p> would be auto-closed by
    // the browser and break word-index alignment.
    return (
      <div className="reading-scroll" ref={readingSwipeRef}>
        <div className="reading-text" data-flip-idx={flipIdx}>
          {body}
        </div>
      </div>
    );
  };

  /* Plain-text fallback: same logic as before, just factored out so the
     rich-html path can share the word counter. */
  function renderPlainText(text, counter, keyPrefix = 'p') {
    if (!text) return null;
    return text.split(/(\s+)/).map((part, i) => {
      if (!part) return null;
      if (/^\s+$/.test(part)) {
        return part.includes('\n')
          ? <br key={`${keyPrefix}-br-${i}`} />
          : <React.Fragment key={`${keyPrefix}-sp-${i}`}>{part}</React.Fragment>;
      }
      const idx = counter.value++;
      return (
        <span key={`${keyPrefix}-w-${i}`} className="tts-word" data-idx={idx}>
          {part}
        </span>
      );
    });
  }

  /* Rich-html renderer. Walks the parsed DOM, wraps each whitespace-
     delimited word in a .tts-word span (so the existing highlighter
     keeps working), and preserves the admin's inline formatting via a
     whitelisted set of tags + style properties. */
  const SAFE_TAGS = new Set([
    'p', 'div', 'span', 'b', 'strong', 'i', 'em', 'u',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'sub', 'sup', 'small', 'mark', 'blockquote',
    'img', 'ul', 'ol', 'li', 'hr',
  ]);
  const SAFE_STYLE_PROPS = new Set([
    'font-family', 'font-size', 'font-weight', 'font-style',
    'text-decoration', 'text-decoration-line',
    'text-align', 'text-indent', 'line-height', 'margin-top', 'margin-bottom', 'list-style-type', 'left', 'top', 'transform',
    'color', 'background-color',
    // Image sizing controls from the editor's Size dropdown — without
    // these the reader strips the inline size override and falls back
    // to the default CSS max-width caps.
    'width', 'height', 'max-width', 'max-height',
  ]);
  const reactStyleKey = (cssProp) =>
    cssProp.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

  function parseInlineStyle(raw) {
    const out = {};
    if (!raw) return out;
    raw.split(';').forEach((decl) => {
      const i = decl.indexOf(':');
      if (i < 0) return;
      const prop = decl.slice(0, i).trim().toLowerCase();
      const val  = decl.slice(i + 1).trim();
      if (!prop || !val) return;
      if (!SAFE_STYLE_PROPS.has(prop)) return;
      if (/(javascript:|expression\(|url\()/i.test(val)) return;
      out[reactStyleKey(prop)] = val;
    });
    return out;
  }

  function walkRichNode(node, counter, keyPrefix) {
    // Text node — split into words / whitespace tokens, increment counter.
    if (node.nodeType === 3) {
      return renderPlainText(node.nodeValue || '', counter, keyPrefix);
    }
    if (node.nodeType !== 1) return null;
    const tagRaw = node.tagName.toLowerCase();
    if (tagRaw === 'hr') return <hr key={keyPrefix} />;
    if (tagRaw === 'br') return <br key={keyPrefix} />;
    // <img> is a void element with no children — handle separately so we
    // pass through src/alt/className (for float alignment) but don't
    // recurse into children (none) or apply text-node walking.
    if (tagRaw === 'img') {
      const src = node.getAttribute('src') || '';
      if (!src) return null;
      const safeSrc = /^javascript:/i.test(src) ? '' : src;
      if (!safeSrc) return null;
      const className = node.getAttribute('class') || '';
      const alt = node.getAttribute('alt') || '';
      const style = parseInlineStyle(node.getAttribute('style') || '');
      const props = { key: keyPrefix, src: safeSrc, alt, draggable: false };
      if (className) props.className = className;
      if (Object.keys(style).length) props.style = style;
      return React.createElement('img', props);
    }
    const tag = SAFE_TAGS.has(tagRaw) ? tagRaw : 'span';
    const style = parseInlineStyle(node.getAttribute('style') || '');
    const className = node.getAttribute('class') || '';

    // Firefly wrapper: normalise to the brushstroke layout (blob +
    // img + 3 sparks). Promotes the inner img's inline width and
    // alignment class onto the wrap so the wrap exactly hugs the
    // image and positions correctly in its paragraph; defaults to
    // green when older saves don't carry a colour class.
    if (tagRaw === 'span' && className.indexOf('rte-img-firefly-wrap') !== -1) {
      const innerImg = node.querySelector && node.querySelector('img');
      if (innerImg) {
        const imgStyle = parseInlineStyle(innerImg.getAttribute('style') || '');
        if (imgStyle.width && !style.width) style.width = imgStyle.width;
        if (imgStyle.maxWidth && !style.maxWidth) style.maxWidth = imgStyle.maxWidth;
        let cls = className;
        if (!/rte-img-firefly-(green|orange|purple)/.test(cls)) {
          cls = `${cls} rte-img-firefly-green`.trim();
        }
        // Promote alignment from the img to the wrap if it isn't
        // already present on the wrap. Defaults to center.
        if (!/rte-img-(left|center|right)\b/.test(cls)) {
          const imgCls = innerImg.getAttribute('class') || '';
          const m = imgCls.match(/rte-img-(left|center|right)\b/);
          cls = `${cls} ${m ? m[0] : 'rte-img-center'}`.trim();
        }
        const imgEl = walkRichNode(innerImg, counter, `${keyPrefix}-img`);
        const props = { key: keyPrefix, className: cls };
        if (Object.keys(style).length) props.style = style;
        return React.createElement(
          tag,
          props,
          React.createElement('span', { key: 'blob', className: 'rte-img-firefly-blob', 'aria-hidden': true }),
          imgEl,
          React.createElement('span', { key: 's1',   className: 'rte-img-firefly-spark', 'aria-hidden': true }),
          React.createElement('span', { key: 's2',   className: 'rte-img-firefly-spark', 'aria-hidden': true }),
          React.createElement('span', { key: 's3',   className: 'rte-img-firefly-spark', 'aria-hidden': true })
        );
      }
    }
    const children = [];
    let i = 0;
    for (const child of node.childNodes) {
      const sub = walkRichNode(child, counter, `${keyPrefix}-${i++}`);
      if (Array.isArray(sub)) children.push(...sub); else if (sub != null) children.push(sub);
    }
    const props = { key: keyPrefix };
    if (className) props.className = className;
    if (Object.keys(style).length) props.style = style;
    return React.createElement(tag, props, ...children);
  }

  function renderRichHtml(html, counter) {
    if (typeof window === 'undefined' || !window.DOMParser) {
      return renderPlainText(html.replace(/<[^>]+>/g, ''), counter, 'rh');
    }
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body && doc.body.firstChild;
    if (!root) return null;
    const out = [];
    let i = 0;
    for (const child of root.childNodes) {
      const sub = walkRichNode(child, counter, `rh-${i++}`);
      if (Array.isArray(sub)) out.push(...sub); else if (sub != null) out.push(sub);
    }
    return out;
  }

  // Extract every <img> from a page's rich HTML for the intro/title page,
  // which renders the page text as a custom <h1> (no walker) and would
  // otherwise drop inline images entirely. Returns full enough info so
  // the intro JSX can rebuild a firefly wrapper if the admin set that
  // effect on the image.
  function extractIntroImages(html) {
    if (!html || typeof window === 'undefined' || !window.DOMParser) return [];
    try {
      const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
      const imgs = Array.from(doc.querySelectorAll('img'));
      return imgs
        .map((el) => {
          const src = el.getAttribute('src') || '';
          if (!src || /^javascript:/i.test(src)) return null;
          const wrap = el.closest && el.closest('.rte-img-firefly-wrap');
          let fireflyColor = null;
          if (wrap) {
            const wc = wrap.getAttribute('class') || '';
            const m = wc.match(/rte-img-firefly-(green|orange|purple)/);
            fireflyColor = m ? m[1] : 'green';
          }
          // Admin's resize writes inline width onto the WRAP when the image
          // is firefly-wrapped, and onto the IMG otherwise. Carry whichever
          // is set so the intro page honours the admin-chosen size — the
          // safe-style parser (parseInlineStyle) drops anything else.
          const wrapStyle = wrap ? parseInlineStyle(wrap.getAttribute('style') || '') : {};
          const imgStyle  = parseInlineStyle(el.getAttribute('style') || '');
          const imageUnit = wrap || el;
          const captionNode = imageUnit.nextElementSibling;
          const caption = captionNode?.classList?.contains('rte-img-caption')
            ? (captionNode.textContent || '').slice(0, 240) : '';
          return {
            src,
            alt: el.getAttribute('alt') || '',
            className: el.getAttribute('class') || '',
            inFirefly: !!wrap,
            fireflyColor,
            wrapStyle,
            imgStyle,
            caption,
          };
        })
        .filter(Boolean);
    } catch { return []; }
  }

  /* ── Build pages for the flipbook ──
     Also builds pageTextMap: flipbook-page-index → text for TTS */
  const pageTextMapRef = useRef([]);

  // Only a book whose format is Landscape reads wide. A portrait book's
  // one-page view is a normal single page; its two-page view is the spread.
  const isWideSinglePage = orientation === 'single' && book?.format === 'landscape';

  const buildPages = () => {
    if (!book) return [];
    const isRtl = lang === 'ar';
    const pages = [];
    const textMap = []; // parallel array: textMap[flipbookIdx] = text for that page
    const pageNumberMap = [];
    const softDensity = orientation === 'single' ? 'hard' : 'soft';

    book.pages.forEach((page) => {
      const text = getText(page);
      const image = getImage(page);
      const isCover = page.pageNumber === 0;

      if (isCover) {
        pages.push(
          <Page key="cover" density="hard" className="page-cover-style">
            <div className="page-cover">
              {image && <img src={image} alt="Cover" className="cover-img" draggable={false} />}
              <div className="page-ornament">&#10040; &#10040; &#10040;</div>
            </div>
          </Page>
        );
        textMap.push(text); // cover title
        pageNumberMap.push(page.pageNumber);

        // Blank flyleaf (left of first spread in double mode)
        pages.push(
          <Page key="blank" density={softDensity}>
            <div className="page-blank" />
          </Page>
        );
        textMap.push('');
        pageNumberMap.push(null);

        // Intro / title page (right of first spread)
        const introText = text;
        const introFlipIdx = pages.length;
        let introWordIdx = 0;
        // Any images the admin inserted into page 0's rich text (intro
        // page is rendered via a custom <h1>, not via renderHighlightedText,
        // so it would otherwise drop the inline <img> tags entirely).
        const introImages = extractIntroImages(getHtml(page));
        pages.push(
          <Page key="intro" density={softDensity}>
            <div className="page-intro-scroll">
              <div className="page-intro" dir={isRtl ? 'rtl' : 'ltr'}>
                <div className="intro-category">
                  {/* Prefer the multi-value genres / categories arrays
                     (admin Details tab); fall back to the legacy
                     singulars; last-resort "Adventure". Multiple values
                     join with ' · ' so e.g. "Islam · Fiction". */}
                  {(() => {
                    const list = (Array.isArray(book.genres) && book.genres.length ? book.genres
                                 : book.genre ? [book.genre]
                                 : Array.isArray(book.categories) && book.categories.length ? book.categories
                                 : book.category ? [book.category]
                                 : []);
                    if (list.length === 0) return isRtl ? 'مغامرة' : 'Adventure';
                    return list.join(' · ');
                  })()}
                </div>
                {introImages.length > 0 && (
                  <div className="intro-images">
                    {introImages.map((img, ii) => {
                      if (img.inFirefly) {
                        const colorCls = `rte-img-firefly-${img.fireflyColor || 'green'}`;
                        // Width on the wrap (admin's resize stores it there
                        // for firefly imgs). Fall back to the img's inline
                        // width for older saves that pre-date the wrap.
                        const wrapStyle = { ...(img.wrapStyle || {}) };
                        if (!wrapStyle.width    && img.imgStyle?.width)    wrapStyle.width    = img.imgStyle.width;
                        if (!wrapStyle.maxWidth && img.imgStyle?.maxWidth) wrapStyle.maxWidth = img.imgStyle.maxWidth;
                        return (
                          <React.Fragment key={`ii-${ii}`}>
                            <span className={`rte-img-firefly-wrap ${colorCls}`} style={wrapStyle}>
                              <span className="rte-img-firefly-blob" aria-hidden="true" />
                              <img src={img.src} alt={img.alt} draggable={false} />
                              <span className="rte-img-firefly-spark" aria-hidden="true" />
                              <span className="rte-img-firefly-spark" aria-hidden="true" />
                              <span className="rte-img-firefly-spark" aria-hidden="true" />
                            </span>
                            {img.caption && <small className="rte-img-caption">{img.caption}</small>}
                          </React.Fragment>
                        );
                      }
                      return (
                        <React.Fragment key={`ii-${ii}`}>
                          <img src={img.src} alt={img.alt} className={img.className || undefined} style={img.imgStyle || undefined} draggable={false} />
                          {img.caption && <small className="rte-img-caption">{img.caption}</small>}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
                <h1 className="intro-title" data-flip-idx={introFlipIdx}>
                  {introText.split(/(\s+)/).map((part, i) => {
                    if (/^\s+$/.test(part)) {
                      return part.includes('\n') ? <br key={`ibr-${i}`} /> : <span key={`isp-${i}`}>{part}</span>;
                    }
                    const idx = introWordIdx++;
                    return (
                      <span key={`iw-${i}`} className="tts-word" data-idx={idx}>
                        {part}
                      </span>
                    );
                  })}
                </h1>
                <button
                  ref={stopFlipPropRef}
                  aria-label={isSpeaking ? 'Stop narration' : 'Play narration'}
                  className={`intro-audio-btn ${isSpeaking ? 'playing' : ''}`}
                  onClick={() => {
                    if (isSpeaking) { stopAudio(); return; }
                    autoAdvanceRef.current = true;
                    lastPlayedTextRef.current = introText;
                    lastPlayedPageRef.current = 0;
                    const r = resumeStateRef.current;
                    const seek = r && r.pageNumber === 0 ? r.time : 0;
                    resumeStateRef.current = null;
                    const firstPage = (book?.pages || []).find(p => p.pageNumber === 0);
                    const manualUrl = firstPage && (lang === 'ar' ? firstPage.audioAr : firstPage.audioEn);
                    if (manualUrl) playPageAudio(manualUrl, introText, seek); else playTTS(introText, seek);
                  }}
                  disabled={ttsLoading}
                >
                  {ttsLoading
                    ? <div className="loading-spinner" style={{width:18,height:18,borderWidth:2}}/>
                    : isSpeaking ? <IconStop/> : <IconSpeaker/>}
                </button>
                <button
                  type="button"
                  ref={stopFlipPropRef}
                  className="intro-lang-btn"
                  onClick={() => switchLang(lang === 'ar' ? 'en' : 'ar')}
                  aria-label={lang === 'ar' ? 'Switch to English' : 'Switch to Arabic'}
                  title={lang === 'ar' ? 'English' : 'العربية'}
                >
                  <span className="intro-lang-btn-text">{lang === 'ar' ? 'EN' : 'ع'}</span>
                </button>
                <div className="intro-drag-hint">{isRtl ? 'اسحب لقراءة القصة' : 'Drag to read story'}</div>
                <div className={`intro-arrow${isRtl ? ' intro-arrow--rtl' : ''}`} aria-hidden="true"><IconDragArrow flip={isRtl} /></div>
              </div>
            </div>
          </Page>
        );
        textMap.push(text); // intro reads title
        pageNumberMap.push(page.pageNumber);
      } else {
        // Illustration page — only emitted in the default 'image-text'
        // layout. The 'text-text' layout drops illustration pages so
        // every flipbook side is reading text (novel-style spread);
        // per-page images for text-text books are inserted inline by
        // the admin via the rich-text editor.
        if (book.pageLayout !== 'text-text') {
          pages.push(
            <Page key={`img-${page.pageNumber}`} density={softDensity}>
              <div className="page-illust">
                {image && <img src={image} alt={`Page ${page.pageNumber}`} className="illust-img" draggable={false} />}
              </div>
            </Page>
          );
          textMap.push(text); // illustration page reads same text
          pageNumberMap.push(page.pageNumber);
        }
        // Text page — pass the flipbook index (pages.length BEFORE push)
        const textFlipIdx = pages.length;
        pages.push(
          <Page key={`txt-${page.pageNumber}`} density={softDensity}>
            <div className="page-reading" dir={isRtl ? 'rtl' : 'ltr'}>
              {renderHighlightedText(page, textFlipIdx)}
              <div className="page-controls">
                {book.format !== 'landscape' && (
                  <div className="orientation-toggle" ref={stopFlipPropRef}>
                    <button className={`orientation-btn ${orientation === 'single' ? 'active' : ''}`} onClick={() => switchOrientation('single')} aria-label="Single page"><IconSinglePage /></button>
                    <button className={`orientation-btn ${orientation === 'double' ? 'active' : ''}`} onClick={() => switchOrientation('double')} aria-label="Double page"><IconDoublePage /></button>
                  </div>
                )}
                <button
                  ref={stopFlipPropRef}
                  aria-label={isSpeaking ? 'Stop narration' : 'Play narration'}
                  className={`page-listen-btn ${isSpeaking ? 'playing' : ''}`}
                  onClick={() => toggleAudio(textFlipIdx)}
                  disabled={ttsLoading}
                >
                  {ttsLoading
                    ? <div className="loading-spinner" style={{width:16,height:16,borderWidth:2}}/>
                    : isSpeaking ? <IconStop/> : <IconPlay/>}
                </button>
                <div className="lang-toggle" ref={stopFlipPropRef}>
                  <button className={`lang-btn ${lang === 'en' ? 'active' : ''}`} onClick={() => switchLang('en')}>EN</button>
                  <button className={`lang-btn ${lang === 'ar' ? 'active' : ''}`} onClick={() => switchLang('ar')}>ع</button>
                </div>
              </div>
            </div>
          </Page>
        );
        textMap.push(text); // text page
        pageNumberMap.push(page.pageNumber);
      }
    });

    if (book?.preview) {
      // Preview-end trigger — replaces the back cover when the visitor
      // is reading a trimmed preview. The user flips through content
      // pages 1+2 with no paywall interruption; the gate only opens
      // when they click forward from page 2 and land on THIS page.
      pages.push(
        <Page key="preview-end" density="hard" className="page-cover-style">
          <div className="page-cover page-back">
            <div className="back-cover-inner">
              <div className="page-ornament">&#10040; &#10040; &#10040;</div>
              <h2 className="cover-title">End of preview</h2>
              <p className="thanks-line">
                Subscribe to read the full book — and the rest of the library.
              </p>
              <button
                type="button"
                ref={stopFlipPropRef}
                className="back-cover-quiz-btn"
                onClick={() => openSubscribeGate(`/read/${book._id}`)}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
                Subscribe to continue
              </button>
              <div className="page-ornament">&#10040; &#10040; &#10040;</div>
            </div>
          </div>
        </Page>
      );
      textMap.push('');
      pageNumberMap.push(null);
    } else {
      pages.push(
        <Page key="back" density="hard" className="page-cover-style">
          <div className="page-cover page-back">
            <div className="back-cover-inner">
              <div className="page-ornament">&#10040; &#10040; &#10040;</div>
              <h2 className="cover-title" dir={isRtl ? 'rtl' : 'ltr'}>
                {lang === 'ar' ? 'النهاية' : 'The End'}
              </h2>
              <p className="thanks-line" dir={isRtl ? 'rtl' : 'ltr'}>
                {lang === 'ar' ? 'شكرًا لقراءتك' : 'Thank you for reading'}
              </p>
              {book?.hasQuiz && (
                <Link
                  ref={stopFlipPropRef}
                  to={`/quiz/${book._id}`}
                  className="back-cover-quiz-btn"
                  dir={isRtl ? 'rtl' : 'ltr'}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="4" width="14" height="17" rx="2" />
                    <path d="M9 4v3h6V4" />
                    <polyline points="9 13 11 15 15 11" />
                  </svg>
                  {lang === 'ar' ? 'ابدأ الاختبار' : 'Take the Quiz'}
                </Link>
              )}
              <div className="page-ornament">&#10040; &#10040; &#10040;</div>
            </div>
          </div>
        </Page>
      );
      textMap.push(''); // back cover
      pageNumberMap.push(null);
    }

    pageTextMapRef.current = textMap;
    pageNumberMapRef.current = pageNumberMap;
    return pages;
  };

  // Memoize pages against everything EXCEPT activeWordIndex/wordTimings so
  // react-pageflip doesn't see new children every ~50ms during TTS playback
  // (which was causing the reading-scroll position to reset). Highlighting
  // is applied imperatively via DOM in the effect below so the memo can stay.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const flipPages = useMemo(
    () => buildPages(),
    [book, lang, orientation, isSpeaking, ttsLoading, currentPage]
  );
  const totalPages = flipPages.length;
  flipPagesLengthRef.current = totalPages;
  const progress = totalPages > 0 ? ((currentPage + 1) / totalPages) * 100 : 0;

  /* ── Render ── */
  useEffect(() => {
    const start = () => {
      if (tourOriginRef.current === null) tourOriginRef.current = currentPageRef.current;
      stopPlaybackRef.current?.();
    };
    const reveal = event => {
      start();
      const selector = event.detail?.selector;
      if (!selector || !interactionReady) return;
      const pages = [...(wrapperRef.current?.querySelectorAll('.stf__item') || [])];
      const target = pages.findIndex(page => page.querySelector(selector));
      const control = target >= 0 ? pages[target].querySelector(selector) : null;
      if (!control || getComputedStyle(control).display === 'none' || (book?.preview && target === pages.length - 1)) {
        event.detail.unavailable = true;
        return;
      }
      if (target >= 0) {
        flipRef.current?.pageFlip()?.turnToPage(target);
      }
    };
    const end = () => {
      const origin = tourOriginRef.current;
      if (origin === null) return;
      flipRef.current?.pageFlip()?.turnToPage(origin);
      currentPageRef.current = origin;
      setCurrentPage(origin);
      tourOriginRef.current = null;
    };
    window.addEventListener('deenlit-reader-tour-start', start);
    window.addEventListener('deenlit-reader-tour-reveal', reveal);
    window.addEventListener('deenlit-reader-tour-end', end);
    return () => {
      window.removeEventListener('deenlit-reader-tour-start', start);
      window.removeEventListener('deenlit-reader-tour-reveal', reveal);
      window.removeEventListener('deenlit-reader-tour-end', end);
    };
  }, [interactionReady, book]);

  if (loading) return (
    <div className="reader-wrapper">
      <div className="reader-loading"><div className="loading-spinner" /><span>Opening book...</span></div>
    </div>
  );

  if (!book) return (
    <div className="reader-wrapper">
      <div className="reader-loading"><span>{loadError === 'not-found' ? 'Book not found' : 'Could not open this book right now.'}</span>{loadError !== 'not-found' && <button type="button" className="back-btn" onClick={() => setLoadAttempt(value => value + 1)}>Try again</button>}<Link to="/category/all" className="back-btn">Return to Library</Link></div>
    </div>
  );

  const bookTitle = lang === 'ar' ? book.titleAr : book.title;
  // A landscape book's single page is wide; portrait devices keep their width.
  const pageWidth = isWideSinglePage && !['phone-portrait', 'tablet-portrait'].includes(layoutMode)
    ? Math.min(dims.w * 2, Math.floor(dims.h * 1.8))
    : dims.w;
  const pageHeight = isWideSinglePage
    ? Math.max(210, Math.min(dims.h, Math.floor(pageWidth / 1.55)))
    : dims.h;

  return (
    <div ref={wrapperRef} className={`reader-wrapper${layoutMode ? ' reader-wrapper--' + layoutMode : ''}${isWideSinglePage ? ' reader-wrapper--landscape-book' : ''}`} data-bg={book.pageBackground || 'cream'}>
      {/* Floating back button — returns to wherever the user came from (Library, Profile, etc.) */}
      <button
        type="button"
        ref={stopFlipPropRef}
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/category/all'))}
        className="floating-back-btn"
      >
        &#8592; Back
      </button>

      {showResumeBanner && savedProgress && (
        <div className="resume-banner" ref={stopFlipPropRef}>
          <span>Resume from page {savedProgress.page + 1}?</span>
          <button type="button" className="resume-banner-btn" onClick={resumeToSaved}>Resume</button>
          <button type="button" className="resume-banner-dismiss" onClick={() => setShowResumeBanner(false)} aria-label="Dismiss">&times;</button>
        </div>
      )}

      {book?.adminPreview && (
        <div className="reader-preview-badge" ref={stopFlipPropRef}>
          <span className="reader-preview-badge-dot" aria-hidden="true" />
          <span className="reader-preview-badge-label">{book.publicationStatus === 'draft' ? 'Admin preview · draft, not visible to readers' : 'Admin preview'}</span>
        </div>
      )}

      {book?.preview && (
        <div className="reader-preview-badge" ref={stopFlipPropRef}>
          <span className="reader-preview-badge-dot" aria-hidden="true" />
          <span className="reader-preview-badge-label">Preview mode</span>
          <button
            type="button"
            className="reader-preview-badge-cta"
            onClick={() => openSubscribeGate(`/read/${id}`)}
          >
            Unlock full book
          </button>
        </div>
      )}


      <div className="reader-utility" ref={stopFlipPropRef}>
        <button type="button" className="reader-fullscreen" aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'} title={isFullscreen ? 'Exit full screen' : 'Full screen'} onClick={() => { if (document.fullscreenElement) document.exitFullscreen?.(); else wrapperRef.current?.requestFullscreen?.().catch(() => {}); }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{isFullscreen ? <path d="M5 5l14 14M19 5L5 19" /> : <path d="M8 3H3v5M16 3h5v5M21 16v5h-5M8 21H3v-5" />}</svg></button>
      </div>
      {isFullscreen && <button type="button" ref={stopFlipPropRef} className="reader-fullscreen-close" aria-label="Close full screen" title="Close full screen" onClick={() => document.exitFullscreen?.()}>×</button>}
      {audioMessage && <p role="status" className="reader-audio-message">{audioMessage}</p>}
      {/* Book scene */}
      <div className="book-scene">
        <button
          type="button" aria-label="Previous page" className="nav-btn nav-prev"
          data-ready={interactionReady ? 'true' : 'false'}
          onClick={goPrev}
          onPointerDown={(e) => {
            const el = e.currentTarget;
            el.classList.add('is-pressed');
            // Fallback: some mobile browsers don't fire pointerup reliably.
            setTimeout(() => el.classList.remove('is-pressed'), 250);
          }}
          onPointerUp={(e) => { e.currentTarget.classList.remove('is-pressed'); e.currentTarget.blur(); }}
          onPointerLeave={(e) => e.currentTarget.classList.remove('is-pressed')}
          onPointerCancel={(e) => e.currentTarget.classList.remove('is-pressed')}
        >&#8249;</button>

        <div className="leather-frame" data-ready={interactionReady ? 'true' : 'false'}>
          <HTMLFlipBook
            ref={flipRef}
            key={`${pageWidth}-${pageHeight}-${orientation}`}
            width={pageWidth}
            height={pageHeight}
            size="fixed"
            showCover={true}
            drawShadow={true}
            maxShadowOpacity={0.5}
            flippingTime={350}
            swipeDistance={18}
            usePortrait={orientation === 'single'}
            useMouseEvents={true}
            showPageCorners={false}
            mobileScrollSupport={false}
            clickEventForward={false}
            onFlip={onFlip}
            onInit={onInit}
            className="the-flipbook"
          >
            {flipPages}
          </HTMLFlipBook>

        </div>

        <button
          type="button" aria-label="Next page" className="nav-btn nav-next"
          data-ready={interactionReady ? 'true' : 'false'}
          onClick={goNext}
          onPointerDown={(e) => {
            const el = e.currentTarget;
            el.classList.add('is-pressed');
            // Fallback: some mobile browsers don't fire pointerup reliably.
            setTimeout(() => el.classList.remove('is-pressed'), 250);
          }}
          onPointerUp={(e) => { e.currentTarget.classList.remove('is-pressed'); e.currentTarget.blur(); }}
          onPointerLeave={(e) => e.currentTarget.classList.remove('is-pressed')}
          onPointerCancel={(e) => e.currentTarget.classList.remove('is-pressed')}
        >&#8250;</button>
      </div>

      {ratingPromptOpen && (
        <div className="rating-prompt" ref={stopFlipPropRef} role="dialog" aria-label="Rate this book">
          <button
            type="button"
            className="rating-prompt-close"
            aria-label="Dismiss"
            onClick={closeRatingPrompt}
          >&times;</button>
          <div className="rating-prompt-title">
            {ratingSavedAt ? 'Thanks for rating!' : 'How did you like this book?'}
          </div>
          <RatingStars value={userRating} onChange={submitRating} size={36} />
          {ratingSavedAt ? (
            <div className="rating-prompt-sub">Tap a different star to change your rating.</div>
          ) : (
            <div className="rating-prompt-sub">Tap a star — 1 (didn't like) to 5 (loved it).</div>
          )}
        </div>
      )}

      {/* Progress */}
      <div className="progress-bar-container">
        <span className="progress-text">Page {currentPage + 1}{totalPages > 0 ? ` / ${totalPages}` : ''}</span>
        <div className="progress-track"><div className="progress-fill" style={{width:`${progress}%`}}/></div>
        <span className="progress-text">{Math.round(progress)}%</span>
      </div>
    </div>
  );
}
